import useRoutesHandler from "@/Hooks/useRouteHandler";
import useToggleHandler from "@/Hooks/useToggleHandler";

import { THookProps } from "@/types/global.types";
import { handleSuccessToast } from "@/UI/Toasts";
import { handleNetworkError } from "@/utility/networkError.utility";
import { useGetAuthUserService } from "@repo/api-services/admin";
import { CACHE_KEYS } from "@repo/api-services/CacheKeys";
import { handleGlobalPostRequest } from "@repo/api-services/changeRequestsGlobal.ts";
import { handleGlobalGetRequestQuery } from "@repo/api-services/getRequestsGlobal.ts";
import URLS from "@repo/api-services/URLS";
import { stringUtility } from "@repo/utilities/string.utility.ts";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  IDownloadCertificateSubmitSchema,
  IRejectionSubmitSchema,
} from "../certificate.types";

interface ICertificateHookProps extends THookProps {
  enableCountQuery?: boolean;
}

export default function usePartnerCertificates({
  enablePagination = false,
  enablePerPage = false,
  enableQuery = true,
  enableCountQuery = false,
}: ICertificateHookProps) {
  // States
  const [errorFile, setErrorFile] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Hooks
  const { urlQueries, navigateTo, navigateBackTo, navigate } =
    useRoutesHandler();

  const [openModal, setOpenModal] = useState<"download" | "upload" | null>(
    null
  );
  const { isOpen, toggler } = useToggleHandler();
  const { authService } = useGetAuthUserService();
  const partnerLoginId = authService?.data?.data?.partner?.id;
  const page = enablePagination ? urlQueries.page || "1" : "1";
  const pageSize = enablePerPage ? urlQueries.pagesize || "15" : "15";
  const certificateId = urlQueries["certificate-id"] || "";
  const coiStatus = urlQueries?.["coi-status"] || "";

  const searchParams = urlQueries["coi-status"]
    ? { "coi-status": urlQueries["coi-status"] }
    : undefined;
  const certificate_no = urlQueries["certificate-no"] ?? "";
  const gross_premium = urlQueries["gross_premium"] ?? "";
  const customer_name = urlQueries["customer-name"] ?? "";
  const years = urlQueries["year"] ?? "2026";

  // Services
  const getAllPartnerCertificatesService = useQuery({
    queryKey: [
      ...CACHE_KEYS.CERTIFICATES,
      page,
      coiStatus,
      pageSize,
      "certificate",
      certificate_no,
      gross_premium,
      customer_name,
    ],
    queryFn: () =>
      handleGlobalGetRequestQuery({
        url: URLS.CERTIFICATES,
        searchParams: {
          "filter[certificate_stage_id]": coiStatus,
          "filter[certificate_no]": certificate_no,
          "filter[total_premium]": gross_premium,
          "filter[customer_name]": customer_name,
          page: page,
          per_page: pageSize,
          include:
            "certificate_stage,enquiry.proposer,partner,enquiry.subjects",
        },
      }),
    enabled: enableQuery,
  });
  // singlePartnerCertificate

  const getSinglePartnerCertificateService = useQuery({
    queryKey: [...CACHE_KEYS.SINGLE_CERTIFICATE, certificateId, page, pageSize],
    queryFn: () =>
      handleGlobalGetRequestQuery({
        url: `${URLS.CERTIFICATES}/${certificateId}`,
        searchParams: {
          include:
            "certificate_stage,enquiry.proposer,partner,certificatable.product.subSegment",
        },
      }),
    enabled: certificateId ? true : false,
  });

  const getAllCertificateStagesService = useQuery({
    queryKey: [...CACHE_KEYS.CERTIFICATES_STAGES],
    queryFn: () =>
      handleGlobalGetRequestQuery({
        url: URLS.CERTIFICATES_STAGES,
        searchParams: {
          include: "certificates_count",
        },
      }),
  });

  const getTotalPremiumCountService = useQuery({
    queryKey: [...CACHE_KEYS.CERTIFICATES_PREMIUM_COUNT, years],
    queryFn: () =>
      handleGlobalGetRequestQuery({
        url: `/certificates/stats/total-premium`,
        searchParams: { by: "month", year: years },
      }),
    enabled: enableCountQuery,
  });

  const getAveragePremiumCountService = useQuery({
    queryKey: [...CACHE_KEYS.CERTIFICATES_AVERAGE_PREMIUM_COUNT],
    queryFn: () =>
      handleGlobalGetRequestQuery({
        url: `/certificates/stats/total-premium`,
        searchParams: { by: "averagePremium" },
      }),
    enabled: enableCountQuery,
  });

  // Mutations

  const postApproveCertificateMutation = useMutation({
    mutationFn: (id: number) => {
      return handleGlobalPostRequest({
        url: `${URLS.CERTIFICATES}/${id}/approve`,
      });
    },
    onSuccess: () => {
      getAllPartnerCertificatesService?.refetch();
      getAllCertificateStagesService?.refetch();
      navigateBackTo({ searchKey: ["certificate-id"] });
      navigateTo({
        url: "/proposal-management/certificates",
        ...(searchParams && { search: searchParams }),
      });
      handleSuccessToast({ message: "Certificate approved successfully" });
    },
    onError: ({ response }: { response: Error }) => {
      handleNetworkError({ response });
    },
  });
  const postRejectCertificateMutation = useMutation({
    mutationFn: (data: IRejectionSubmitSchema) => {
      return handleGlobalPostRequest({
        url: `${URLS.CERTIFICATES}/${data?.id}/reject`,
        data: {
          rejection_note: data.reason,
        },
      });
    },
    onSuccess: () => {
      getAllPartnerCertificatesService?.refetch();
      getAllCertificateStagesService?.refetch();
      navigateBackTo({ searchKey: ["certificate-id"] });
      navigateTo({
        ...(searchParams && { search: searchParams }),
        url: "/proposal-management/certificates",
      });
      handleSuccessToast({ message: "Certificate rejected successfully" });
    },
    onError: ({ response }: { response: Error }) => {
      handleNetworkError({ response });
    },
  });

  const postCancellationReasonMutation = useMutation({
    mutationFn: (data: IRejectionSubmitSchema) => {
      return handleGlobalPostRequest({
        url: `${URLS.CERTIFICATES}/${certificateId}/cancel`,
        data: {
          cancellation_reason_id: data.reason,
        },
      });
    },
    onSuccess: () => {
      getAllPartnerCertificatesService?.refetch();
      getAllCertificateStagesService?.refetch();
      navigateBackTo({ searchKey: ["certificate-id"] });
      navigateTo({
        ...(searchParams && { search: searchParams }),
        url: "/proposal-management/certificates",
      });
      handleSuccessToast({
        message: "Cancellation request raised successfully",
      });
    },
    onError: ({ response }: { response: Error }) => {
      handleNetworkError({ response });
    },
  });

  // function

  const handleApproveCertificate = (id: number) => {
    postApproveCertificateMutation.mutate(id);
  };

  const handleCancellationReason = (data: IRejectionSubmitSchema) => {
    if (data.certificate_stage?.id === 2) {
      postRejectCertificateMutation.mutate(data);
      return;
    }
    postCancellationReasonMutation.mutate(data);
  };

  const handleDownloadCertificate = (
    data: IDownloadCertificateSubmitSchema
  ) => {
    handleGlobalGetRequestQuery({
      url: `${URLS.CERTIFICATES}/download/${data?.type?.code}`,
      searchParams: {
        "filter[partner_id]": data?.partner?.id || "",
        "filter[certificatable_id]": data?.policy?.id || data?.product?.id,
        "filter[certificatable_type]": data?.certificate_type?.code,
        "filter[certificate_stage_id]": data?.certificate_stage?.id || "",
        from: `${stringUtility.removeCharAddSpace(fromDate, "T")}:00`,
        to: `${stringUtility.removeCharAddSpace(toDate, "T")}:00`,
      },
    }).then((response) => {
      const link = response?.data?.link;
      window.open(link, "_blank");
      setOpenModal(null);
      navigate("/proposal-management/certificates");
      handleSuccessToast({ message: "Certificate download successfully" });
    });
  };

  return {
    services: {
      getAllPartnerCertificatesService,
      getAllCertificateStagesService,
      getTotalPremiumCountService,
      getSinglePartnerCertificateService,
      getAveragePremiumCountService,
    },
    mutations: {
      postApproveCertificateMutation,
      postCancellationReasonMutation,
      postRejectCertificateMutation,
    },
    states: {
      isOpen,
      errorFile,
      setErrorFile,
      partnerLoginId,
      openModal,
      setOpenModal,
    },
    function: {
      toggler,
      handleCancellationReason,
      handleApproveCertificate,
      handleDownloadCertificate,
      setFromDate,
      setToDate,
    },
  };
}
